/**
 * ==========================================================================
 * BAGSANDGO HEADER - UNIFIED JAVASCRIPT
 * ==========================================================================
 * 
 * Modern header with smart scroll behavior, mega menus, and mobile optimization
 * BEM Architecture & Performance Optimized
 * 
 * @package BagsAndGo
 * @version 2.0.0
 */

(function() {
    'use strict';

    // ==========================================
    // DOM ELEMENTS
    // ==========================================
    const DOM = {
        header: null,
        headerInner: null,
        headerMain: null,
        promoBanner: null,
        mobileMenuToggle: null,
        mobileMenu: null,
        mobileMenuOverlay: null,
        mobileMenuClose: null,
        dropdownItems: null,
        body: document.body
    };

    // ==========================================
    // STATE
    // ==========================================
    const state = {
        lastScrollTop: 0,
        isScrolling: false,
        isMobileMenuOpen: false,
        scrollThreshold: 10, // px mínimos para activar scroll
        headerTotalHeight: 80 // Solo header main (sin banner)
    };

    /**
     * ==========================================
     * INITIALIZATION
     * ==========================================
     */
    function init() {
        // Cache DOM elements
        DOM.header = document.getElementById('site-header');
        DOM.headerInner = DOM.header?.querySelector('.site-header__inner');
        DOM.headerMain = document.getElementById('header-main');
        DOM.promoBanner = document.getElementById('promo-banner');
        DOM.mobileMenuToggle = document.getElementById('mobile-menu-toggle');
        DOM.mobileMenu = document.getElementById('mobile-menu');
        DOM.mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
        DOM.mobileMenuClose = document.getElementById('mobile-menu-close');
        DOM.dropdownItems = document.querySelectorAll('.nav__item--dropdown');

        if (!DOM.header) {
            console.error('❌ Header: #site-header no encontrado');
            return;
        }

        // Initialize modules
        initSmartScroll();
        initMobileMenu();
        initDropdownMenus();
        initCitySelector();
        initClickOutside();
        initAccessibility();
        
        console.log('✅ Header: Inicializado correctamente (versión unificada)');
    }

    /**
     * ==========================================
     * SMART SCROLL BEHAVIOR
     * ==========================================
     * Hide header on scroll down, show on scroll up
     */
    function initSmartScroll() {
        if (!DOM.headerInner) return;

        let ticking = false;

        function updateHeaderOnScroll() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollDiff = Math.abs(scrollTop - state.lastScrollTop);

            // Ignorar movimientos muy pequeños
            if (scrollDiff < state.scrollThreshold) {
                ticking = false;
                return;
            }

            // En el top de la página: siempre mostrar
            if (scrollTop <= state.headerTotalHeight) {
                DOM.header.classList.remove('is-hidden');
                DOM.header.classList.remove('is-scrolled-up');
                DOM.header.classList.remove('is-scrolled');
            } 
            // Scrolling hacia ABAJO: ocultar header
            else if (scrollTop > state.lastScrollTop) {
                DOM.header.classList.add('is-hidden');
                DOM.header.classList.remove('is-scrolled-up');
                DOM.header.classList.add('is-scrolled');
            } 
            // Scrolling hacia ARRIBA: mostrar header
            else {
                DOM.header.classList.remove('is-hidden');
                DOM.header.classList.add('is-scrolled-up');
                DOM.header.classList.add('is-scrolled');
            }

            state.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
            ticking = false;
        }

        function requestTick() {
            if (!ticking) {
                window.requestAnimationFrame(updateHeaderOnScroll);
                ticking = true;
            }
        }

        // Event listeners
        window.addEventListener('scroll', requestTick, { passive: true });
        window.addEventListener('resize', requestTick, { passive: true });
        
        console.log('✅ Smart Scroll: Activado');
    }

    /**
     * ==========================================
     * MOBILE MENU - SIMPLE & FUNCTIONAL REBUILD
     * ==========================================
     */
    function initMobileMenu() {
        const hamburger = document.querySelector('.header-actions__mobile-toggle');
        const overlay = document.querySelector('.mobile-overlay');
        const panel = document.querySelector('.mobile-panel');
        const closeBtn = document.querySelector('.mobile-panel__close');

        if (!hamburger || !overlay || !panel) {
            console.log('⚠️ Mobile menu: Elementos no encontrados');
            return;
        }

        // Abrir menú
        hamburger.addEventListener('click', function(e) {
            e.preventDefault();
            document.body.classList.add('mobile-menu-open');
        });

        // Cerrar menú
        const closeMobileMenu = function(e) {
            e.preventDefault();
            document.body.classList.remove('mobile-menu-open');
        };

        if (closeBtn) closeBtn.addEventListener('click', closeMobileMenu);
        if (overlay) overlay.addEventListener('click', closeMobileMenu);

        // ESC key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && document.body.classList.contains('mobile-menu-open')) {
                document.body.classList.remove('mobile-menu-open');
            }
        });

        // Mobile City Buttons
        const mobileCityBtns = document.querySelectorAll('.mobile-city-btn');
        if (mobileCityBtns.length > 0) {
            mobileCityBtns.forEach(function(btn) {
                btn.addEventListener('click', function(e) {
                    e.preventDefault();
                    
                    const newCity = this.getAttribute('data-city');
                    if (!newCity) return;
                    
                    // Update active state visually
                    mobileCityBtns.forEach(b => b.classList.remove('active'));
                    this.classList.add('active');
                    
                    // If on home page, use the global city switcher
                    if (document.body.classList.contains('home')) {
                        // Save to localStorage and dispatch event
                        localStorage.setItem('bagsandgo_selected_city', newCity);
                        document.dispatchEvent(new CustomEvent('bagsandgo:cityChanged', {
                            detail: { city: newCity }
                        }));
                        
                        // Update header city selector
                        const headerCityText = document.getElementById('current-city');
                        if (headerCityText) {
                            const cityNames = { barcelona: 'Barcelona', valencia: 'Valencia' };
                            headerCityText.textContent = cityNames[newCity] || newCity;
                        }
                        
                        // Close mobile menu
                        document.body.classList.remove('mobile-menu-open');
                    } else {
                        // Navigate to new city version of current page
                        const currentPath = window.location.pathname;
                        const currentCities = ['barcelona', 'valencia', 'madrid'];
                        let pathParts = currentPath.split('/').filter(p => p !== '');
                        let langPrefix = '';
                        let restOfPath = [];
                        
                        // Detect language prefix
                        if (['es', 'ca', 'en'].includes(pathParts[0])) {
                            langPrefix = pathParts[0];
                            pathParts = pathParts.slice(1);
                        }
                        
                        // Detect current city in path
                        if (currentCities.includes(pathParts[0])) {
                            restOfPath = pathParts.slice(1);
                        } else {
                            restOfPath = pathParts;
                        }
                        
                        // Build new URL
                        let newPath = '/';
                        if (langPrefix) {
                            newPath += langPrefix + '/';
                        }
                        newPath += newCity + '/';
                        
                        if (restOfPath.length > 0) {
                            newPath += restOfPath.join('/') + '/';
                        }
                        
                        window.location.href = newPath;
                    }
                });
            });
            
            // Sync mobile city buttons with localStorage on load
            const savedCity = localStorage.getItem('bagsandgo_selected_city');
            if (savedCity) {
                mobileCityBtns.forEach(btn => {
                    btn.classList.toggle('active', btn.getAttribute('data-city') === savedCity);
                });
            }
        }

        console.log('✅ Mobile Menu: Inicializado (simple version)');
    }

    /**
     * ==========================================
     * DESKTOP DROPDOWN MENUS
     * ==========================================
     */
    function initDropdownMenus() {
        if (!DOM.dropdownItems || DOM.dropdownItems.length === 0) return;

        DOM.dropdownItems.forEach(function(item) {
            const toggle = item.querySelector('.nav__link');
            const dropdown = item.querySelector('.nav__dropdown');
            
            if (!toggle || !dropdown) return;

            let hoverTimeout;

            // Mouse enter
            item.addEventListener('mouseenter', function() {
                clearTimeout(hoverTimeout);
                openDropdown(item);
            });

            // Mouse leave
            item.addEventListener('mouseleave', function() {
                hoverTimeout = setTimeout(() => {
                    closeDropdown(item);
                }, 150);
            });

            // Click toggle (touch devices)
            toggle.addEventListener('click', function(e) {
                if (window.innerWidth <= 1024) {
                    e.preventDefault();
                    
                    if (item.classList.contains('is-open')) {
                        closeDropdown(item);
                    } else {
                        // Close other dropdowns
                        DOM.dropdownItems.forEach(function(otherItem) {
                            if (otherItem !== item) {
                                closeDropdown(otherItem);
                            }
                        });
                        openDropdown(item);
                    }
                }
            });

            // Keyboard navigation
            toggle.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    
                    if (item.classList.contains('is-open')) {
                        closeDropdown(item);
                    } else {
                        openDropdown(item);
                        
                        // Focus first dropdown item
                        const firstItem = dropdown.querySelector('a, button');
                        if (firstItem) {
                            setTimeout(() => firstItem.focus(), 100);
                        }
                    }
                }
            });
        });

        console.log('✅ Dropdowns: Inicializados');
    }

    function openDropdown(item) {
        item.classList.add('is-open');
        
        const toggle = item.querySelector('.nav__link');
        if (toggle) {
            toggle.setAttribute('aria-expanded', 'true');
        }
    }

    function closeDropdown(item) {
        item.classList.remove('is-open');
        
        const toggle = item.querySelector('.nav__link');
        if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
        }
    }

    /**
     * ==========================================
     * CITY SELECTOR (in header actions)
     * ==========================================
     * En HOME: actualiza contenido dinámicamente
     * En otras páginas: navega a la versión de la ciudad
     */
    function initCitySelector() {
        const container = document.querySelector('.header-actions__city');
        const toggle = document.getElementById('city-toggle');
        const dropdown = document.getElementById('city-dropdown');
        const currentCitySpan = document.getElementById('current-city');
        
        if (!toggle || !dropdown || !container) {
            console.log('⚠️ City Selector: Elementos no encontrados');
            return;
        }

        // Click toggle
        toggle.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            container.classList.toggle('is-open');
        });
        
        // Close on outside click
        document.addEventListener('click', function(e) {
            if (!container.contains(e.target)) {
                container.classList.remove('is-open');
            }
        });

        // City option click
        const cityOptions = dropdown.querySelectorAll('.city__item:not(.city__item--disabled)');
        cityOptions.forEach(function(option) {
            option.addEventListener('click', function(e) {
                e.preventDefault();
                
                const newCity = this.getAttribute('data-city');
                if (!newCity) return;
                
                const currentPath = window.location.pathname;
                const isHomePage = currentPath === '/' || currentPath === '';
                
                // Cerrar dropdown
                container.classList.remove('is-open');
                
                // Si estamos en la HOME, cambiar dinámicamente SIN redirect
                if (isHomePage) {
                    // Usar el sistema centralizado de ciudad
                    if (window.BagsAndGoCitySwitcher) {
                        window.BagsAndGoCitySwitcher.setCity(newCity, { source: 'header' });
                    }
                    
                    // Actualizar UI del header
                    updateDropdownActive(newCity);
                    if (currentCitySpan) {
                        currentCitySpan.textContent = newCity.charAt(0).toUpperCase() + newCity.slice(1);
                    }
                    
                    // Disparar evento para que front-page.php actualice todo
                    document.dispatchEvent(new CustomEvent('bagsandgo:cityChanged', {
                        detail: { city: newCity, source: 'header' }
                    }));
                    
                    return;
                }
                
                // Si estamos en otra página, navegar a la versión de esa ciudad
                const currentCities = ['barcelona', 'valencia', 'madrid'];
                let pathParts = currentPath.split('/').filter(p => p !== '');
                let langPrefix = '';
                let restOfPath = [];
                
                // Detectar si hay prefijo de idioma
                if (['es', 'ca', 'en'].includes(pathParts[0])) {
                    langPrefix = pathParts[0];
                    pathParts = pathParts.slice(1);
                }
                
                // Detectar ciudad actual en el path
                if (currentCities.includes(pathParts[0])) {
                    restOfPath = pathParts.slice(1);
                } else {
                    restOfPath = pathParts;
                }
                
                // Guardar la ciudad en cookie antes de navegar
                if (window.BagsAndGoCitySwitcher) {
                    window.BagsAndGoCitySwitcher.setCity(newCity, { 
                        source: 'header',
                        skipEvent: true 
                    });
                }
                
                // Construir nueva URL
                let newPath = '/';
                if (langPrefix) {
                    newPath += langPrefix + '/';
                }
                newPath += newCity + '/';
                
                if (restOfPath.length > 0) {
                    newPath += restOfPath.join('/') + '/';
                }
                
                window.location.href = newPath;
            });
        });
        
        // Escuchar cambios de ciudad desde otros componentes
        document.addEventListener('bagsandgo:cityChanged', function(e) {
            const city = e.detail?.city;
            const source = e.detail?.source;
            
            // Si el cambio viene de otro lugar, actualizar el header
            if (city && source !== 'header') {
                updateDropdownActive(city);
                if (currentCitySpan) {
                    currentCitySpan.textContent = city.charAt(0).toUpperCase() + city.slice(1);
                }
            }
        });
        
        // Actualizar contenido del hero en la home
        function updateHomeContent(city) {
            const cityContentEl = document.getElementById('city-content-data');
            if (!cityContentEl) return;
            
            try {
                const cityContent = JSON.parse(cityContentEl.textContent);
                const content = cityContent[city];
                
                if (!content) return;
                
                // Actualizar título (solo la ciudad)
                const heroCity = document.getElementById('hero-city');
                if (heroCity) {
                    heroCity.textContent = content.city_name;
                }
                
                // Actualizar subtítulo
                const heroSubtitle = document.getElementById('hero-subtitle');
                if (heroSubtitle) {
                    heroSubtitle.textContent = content.subtitle;
                }
                
                // Actualizar descripción
                const heroDescription = document.getElementById('hero-description');
                if (heroDescription) {
                    heroDescription.textContent = content.description;
                }
                
                // Actualizar el selector del header
                if (currentCitySpan) {
                    currentCitySpan.textContent = city.charAt(0).toUpperCase() + city.slice(1);
                }
                
                // Actualizar el dropdown de Locations
                updateLocationsDropdown(city);
                
                // Guardar en localStorage
                localStorage.setItem('bagsandgo_selected_city', city);
                
                // Disparar evento para otros componentes
                document.dispatchEvent(new CustomEvent('bagsandgo:cityChanged', {
                    detail: { city: city }
                }));
                
                console.log('✅ Hero actualizado para:', city);
                
            } catch (error) {
                console.error('Error actualizando contenido:', error);
            }
        }
        
        // Actualizar dropdown de Locations dinámicamente
        function updateLocationsDropdown(city) {
            const locationsGrid = document.getElementById('locations-grid');
            const locationsDataEl = document.getElementById('locations-data');
            
            if (!locationsGrid || !locationsDataEl) return;
            
            try {
                const locationsData = JSON.parse(locationsDataEl.textContent);
                const locations = locationsData[city] || locationsData['barcelona'];
                
                // Limpiar grid actual
                locationsGrid.innerHTML = '';
                locationsGrid.dataset.currentCity = city;
                
                // Reconstruir locations
                locations.forEach(function(loc) {
                    const row = document.createElement('div');
                    row.className = 'location__row';
                    row.innerHTML = `
                        <div class="location__info">
                            <span class="location__icon">${loc.icon}</span>
                            <span class="location__name">${loc.name}</span>
                        </div>
                        <div class="location__actions">
                            <a href="/${city}/luggage-transfer/${loc.slug}/" class="location__action location__action--transfer" data-location="${loc.slug}">Transfer</a>
                            <a href="/${city}/luggage-storage/${loc.slug}/" class="location__action location__action--storage" data-location="${loc.slug}">Storage</a>
                        </div>
                    `;
                    locationsGrid.appendChild(row);
                });
                
                console.log('✅ Locations dropdown actualizado para:', city);
                
            } catch (error) {
                console.error('Error actualizando locations:', error);
            }
        }
        
        // Actualizar estado activo en dropdown
        function updateDropdownActive(city) {
            dropdown.querySelectorAll('.city__item').forEach(function(item) {
                const itemCity = item.getAttribute('data-city');
                item.classList.remove('city__item--active');
                
                // Quitar check anterior
                const oldCheck = item.querySelector('.city__check');
                if (oldCheck) oldCheck.remove();
                
                if (itemCity === city) {
                    item.classList.add('city__item--active');
                    // Añadir check
                    const check = document.createElement('span');
                    check.className = 'city__check';
                    check.textContent = '✓';
                    item.appendChild(check);
                }
            });
        }

        // Keyboard navigation
        toggle.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle.click();
            }
            if (e.key === 'Escape') {
                container.classList.remove('is-open');
            }
        });
        
        console.log('✅ City Selector: Inicializado');
    }

    /**
     * ==========================================
     * CLICK OUTSIDE
     * ==========================================
     */
    function initClickOutside() {
        document.addEventListener('click', function(e) {
            if (!DOM.dropdownItems) return;
            
            DOM.dropdownItems.forEach(function(item) {
                if (!item.contains(e.target)) {
                    closeDropdown(item);
                }
            });
        });
    }

    /**
     * ==========================================
     * ACCESSIBILITY
     * ==========================================
     */
    function initAccessibility() {
        // Skip to content link
        const skipLink = document.createElement('a');
        skipLink.href = '#main';
        skipLink.textContent = 'Skip to main content';
        skipLink.className = 'skip-link';
        skipLink.style.cssText = `
            position: absolute;
            left: -9999px;
            top: 0;
            z-index: 999999;
            padding: 8px 16px;
            background: #000;
            color: #fff;
            text-decoration: none;
            transition: left 0.3s;
        `;
        
        skipLink.addEventListener('focus', function() {
            this.style.left = '6px';
            this.style.top = '6px';
        });
        
        skipLink.addEventListener('blur', function() {
            this.style.left = '-9999px';
        });
        
        DOM.body.insertBefore(skipLink, DOM.body.firstChild);
    }

    /**
     * ==========================================
     * START APPLICATION
     * ==========================================
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
    
    // Listen for city change events from other components
    document.addEventListener('bagsandgo:cityChanged', function(e) {
        const newCity = e.detail?.city;
        if (!newCity) return;
        
        // Sync mobile city buttons
        const mobileCityBtns = document.querySelectorAll('.mobile-city-btn');
        mobileCityBtns.forEach(btn => {
            btn.classList.toggle('active', btn.getAttribute('data-city') === newCity);
        });
        
        // Sync header city text
        const headerCityText = document.getElementById('current-city');
        if (headerCityText) {
            const cityNames = { barcelona: 'Barcelona', valencia: 'Valencia' };
            headerCityText.textContent = cityNames[newCity] || newCity;
        }
        
        // Sync header city dropdown items
        document.querySelectorAll('.city__item').forEach(item => {
            item.classList.toggle('city__item--active', item.dataset.city === newCity);
        });
    });

    // Expose API to global scope if needed
    window.BagsAndGoHeader = {
        openDropdown: openDropdown,
        closeDropdown: closeDropdown
    };

})();
